import { useEffect, useState } from "react";
import {
  GoogleAuthProvider,
  OAuthProvider,
  getAuth,
  getRedirectResult,
  onAuthStateChanged,
  signInWithPopup,
  signInWithRedirect,
  signOut,
  type Auth,
  type User,
} from "firebase/auth";

import { getWordinariusFirebaseApp } from "./firebaseApp";

export type WebAuthStatus = "loading" | "signed_out" | "signed_in" | "error";
export type WebAuthErrorCode =
  | "auth/account-exists-with-different-credential"
  | "auth/network-request-failed"
  | "auth/operation-not-allowed"
  | "auth/popup-blocked"
  | "auth/popup-closed-by-user"
  | "auth/unauthorized-domain"
  | "auth/web-storage-unsupported"
  | "auth_state_failed"
  | "firebase_unavailable"
  | "sign_in_failed";

type WebAuthMessageLanguage = "ar" | "en" | "tr";

const WEB_AUTH_ERROR_MESSAGES: Record<WebAuthMessageLanguage, Record<WebAuthErrorCode, string>> = {
  en: {
    "auth/account-exists-with-different-credential": "This email already uses another sign-in method. Continue with the provider you used before.",
    "auth/network-request-failed": "Sign-in could not reach the network. Check your connection and try again.",
    "auth/operation-not-allowed": "This sign-in method is not available right now. Please try another provider.",
    "auth/popup-blocked": "Your browser blocked the sign-in window. Allow pop-ups for this site and try again.",
    "auth/popup-closed-by-user": "The sign-in window was closed before the process finished.",
    "auth/unauthorized-domain": "Sign-in is not enabled for this web address yet. Please open the app from wordinarius.com/app.",
    "auth/web-storage-unsupported": "Sign-in needs browser storage. Enable cookies and site storage, then try again.",
    auth_state_failed: "Your account session could not be read. Refresh the page and try again.",
    firebase_unavailable: "Account services are temporarily unavailable. Please try again later.",
    sign_in_failed: "Sign-in could not be completed. Please try again.",
  },
  tr: {
    "auth/account-exists-with-different-credential": "Bu e-posta başka bir giriş yöntemiyle kullanılıyor. Daha önce kullandığın sağlayıcıyla devam et.",
    "auth/network-request-failed": "Giriş sırasında ağa ulaşılamadı. Bağlantını kontrol edip yeniden dene.",
    "auth/operation-not-allowed": "Bu giriş yöntemi şu anda kullanılamıyor. Lütfen diğer sağlayıcıyı dene.",
    "auth/popup-blocked": "Tarayıcı giriş penceresini engelledi. Bu site için açılır pencerelere izin verip yeniden dene.",
    "auth/popup-closed-by-user": "Giriş tamamlanmadan pencere kapatıldı.",
    "auth/unauthorized-domain": "Bu web adresinde giriş henüz etkin değil. Uygulamayı wordinarius.com/app adresinden aç.",
    "auth/web-storage-unsupported": "Giriş için tarayıcı depolaması gerekiyor. Çerezlere ve site verilerine izin verip yeniden dene.",
    auth_state_failed: "Hesap oturumu okunamadı. Sayfayı yenileyip yeniden dene.",
    firebase_unavailable: "Hesap hizmetlerine geçici olarak ulaşılamıyor. Lütfen daha sonra yeniden dene.",
    sign_in_failed: "Giriş tamamlanamadı. Lütfen yeniden dene.",
  },
  ar: {
    "auth/account-exists-with-different-credential": "هذا البريد مستخدم بطريقة تسجيل دخول أخرى. تابع باستخدام المزوّد الذي استخدمته سابقا.",
    "auth/network-request-failed": "تعذر الوصول إلى الشبكة أثناء تسجيل الدخول. تحقق من اتصالك وحاول مجددا.",
    "auth/operation-not-allowed": "طريقة تسجيل الدخول هذه غير متاحة الآن. جرّب المزوّد الآخر.",
    "auth/popup-blocked": "حظر المتصفح نافذة تسجيل الدخول. اسمح بالنوافذ المنبثقة لهذا الموقع ثم حاول مجددا.",
    "auth/popup-closed-by-user": "أغلقت نافذة تسجيل الدخول قبل اكتمال العملية.",
    "auth/unauthorized-domain": "تسجيل الدخول غير مفعّل لهذا العنوان بعد. افتح التطبيق من wordinarius.com/app.",
    "auth/web-storage-unsupported": "يتطلب تسجيل الدخول تخزين المتصفح. اسمح بملفات الارتباط وبيانات الموقع ثم حاول مجددا.",
    auth_state_failed: "تعذر قراءة جلسة حسابك. حدّث الصفحة وحاول مجددا.",
    firebase_unavailable: "خدمات الحساب غير متاحة مؤقتا. حاول لاحقا.",
    sign_in_failed: "تعذر إكمال تسجيل الدخول. حاول مجددا.",
  },
};

const KNOWN_WEB_AUTH_ERROR_CODES = new Set<WebAuthErrorCode>([
  "auth/account-exists-with-different-credential",
  "auth/network-request-failed",
  "auth/operation-not-allowed",
  "auth/popup-blocked",
  "auth/popup-closed-by-user",
  "auth/unauthorized-domain",
  "auth/web-storage-unsupported",
  "auth_state_failed",
  "firebase_unavailable",
  "sign_in_failed",
]);

export function normalizeWebAuthError(error: unknown): WebAuthErrorCode {
  const rawCode = typeof error === "object" && error && "code" in error
    ? String(error.code || "").trim().toLowerCase()
    : error instanceof Error
      ? error.message.trim().toLowerCase()
      : "";
  if (KNOWN_WEB_AUTH_ERROR_CODES.has(rawCode as WebAuthErrorCode)) {
    return rawCode as WebAuthErrorCode;
  }
  return "sign_in_failed";
}

export function describeWebAuthError(
  errorCode: string | null,
  language: WebAuthMessageLanguage,
): string {
  const normalized = KNOWN_WEB_AUTH_ERROR_CODES.has(errorCode as WebAuthErrorCode)
    ? errorCode as WebAuthErrorCode
    : "sign_in_failed";
  return WEB_AUTH_ERROR_MESSAGES[language][normalized];
}

function getWebAuth(): Auth {
  const app = getWordinariusFirebaseApp();
  if (!app) {
    throw new Error("firebase_unavailable");
  }
  return getAuth(app);
}

async function signInWithProvider(provider: GoogleAuthProvider | OAuthProvider): Promise<void> {
  const auth = getWebAuth();
  try {
    await signInWithPopup(auth, provider);
  } catch (error) {
    const code = typeof error === "object" && error && "code" in error ? String(error.code) : "";
    if (code.includes("popup-blocked") || code.includes("operation-not-supported")) {
      await signInWithRedirect(auth, provider);
      return;
    }
    throw error;
  }
}

export function useWebAuth() {
  const [user, setUser] = useState<User | null>(null);
  const [status, setStatus] = useState<WebAuthStatus>("loading");
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const auth = getWebAuth();
    void getRedirectResult(auth).catch((redirectError) => setError(normalizeWebAuthError(redirectError)));
    return onAuthStateChanged(
      auth,
      (nextUser) => {
        setUser(nextUser);
        setStatus(nextUser ? "signed_in" : "signed_out");
      },
      (authError) => {
        const normalizedError = normalizeWebAuthError(authError);
        setStatus("error");
        setError(normalizedError === "sign_in_failed"
          ? "auth_state_failed"
          : normalizedError);
      },
    );
  }, []);

  const run = async (operation: () => Promise<void>) => {
    setError(null);
    try {
      await operation();
    } catch (operationError) {
      setError(normalizeWebAuthError(operationError));
    }
  };

  return {
    error,
    status,
    user,
    signInWithGoogle: () => run(async () => {
      const provider = new GoogleAuthProvider();
      provider.setCustomParameters({ prompt: "select_account" });
      await signInWithProvider(provider);
    }),
    signInWithApple: () => run(async () => {
      const provider = new OAuthProvider("apple.com");
      provider.addScope("email");
      provider.addScope("name");
      await signInWithProvider(provider);
    }),
    signOut: () => run(() => signOut(getWebAuth())),
  };
}
